import React, { useState } from 'react';
import { useAsync } from 'react-use';
import * as R from 'ramda';
import * as Sentry from '@sentry/browser';
import { Box, Checkbox, FormControlLabel } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { openManagerOverride } from 'containers/Forms/ManagerOverride/actions';
import { PosPlugin } from 'plugins/plugin';
import {
  getHasProductsInShoppingCart,
  getProductInOrderByIndex,
  getProductsInShoppingCart,
  getShoppingCartForSalesDocument,
} from 'reducers/ShoppingCart';
import { getUserGroups } from 'services/ErplyAPI/api';
import Loader from 'components/Loader';
import InputField from 'components/FieldTypes/InputField';
import UIButton from 'components/UIElements/UIButton';
import { getRightsForUserID } from 'actions/Login';
import { RootState } from 'reducers';

import { configurableActions, pluginID } from './constants';
import { Configuration } from './types';
import {
  getConfig,
  getIsOverrideActionEnabled,
  defaultActionsConfig,
} from './selectors';
import { useDispatch, useSelector } from 'react-redux';
import {
  getClientCode,
  getLoggedInEmployeeID,
  getSessionKey,
} from 'reducers/Login';
import { spreadCart } from 'utils/apiTransformations/saveSalesDocumentCart';
import { getSelectedPosID } from 'reducers/PointsOfSale';
import { getSelectedCustomerID } from 'reducers/customerSearch';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { addError, addWarning, dismissType } from 'actions/Error';
import { getCurrentSalesDocument } from 'reducers/sales';
import { resetShoppingCart } from 'actions/ShoppingCart';
import { proxy } from 'services/shared';
import description from './description.md';
import { createSelector } from 'reselect';
import { GridButtonFunction } from 'reducers/UI/gridButtons';
import { fetchPendingSalesAndOpenModal } from 'actions/sales';
import { previousModalPage } from 'actions/ModalPage/previousModalPage';
import { modalPages } from 'constants/modalPage';

function useLatest<T>(value: T) {
  const ref = React.useRef(value);
  React.useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

function SaveCartOnAttemptedBypass() {
  const dispatch: ThunkDispatch<any, any, Action> = useDispatch();
  const cart = useSelector(getShoppingCartForSalesDocument);
  const clientCode = useSelector(getClientCode);
  const sessionKey = useSelector(getSessionKey);
  const warehouseID = useSelector(getSelectedWarehouseID);
  const pointOfSaleID = useSelector(getSelectedPosID);
  const customerID = useSelector(getSelectedCustomerID);
  const employeeID = useSelector(getLoggedInEmployeeID);
  const doc = useSelector(getCurrentSalesDocument);
  const documentID = doc.id ?? doc.invoiceID;

  /** saveSalesDocument request for the current state of the shopping cart */
  const requestBody = React.useMemo(() => {
    if (cart.length === 0) {
      return undefined;
    }
    const searchParams = new URLSearchParams();
    const jsonData = spreadCart({
      // Using current credentials...
      clientCode,
      sessionKey,
      // ...update the salesdoc with the new cart...
      request: 'saveSalesDocument',
      id: documentID,
      cart,
      // ...and leave a little marker to indicate that the user
      // tried to bypass restrictions by refreshing the POS
      attributeName1: 'userClosedPos',
      attributeType1: 'int',
      attributeValue1: 1,
    });
    if (!documentID) {
      // If dealing with a new sale, save it as a new pending cashinvoice
      Object.assign(jsonData, {
        invoiceState: 'PENDING',
        type: 'CASHINVOICE',
        confirmInvoice: 0,
        pointOfSaleID,
        customerID,
        employeeID,
        warehouseID,
      });
    }
    Object.entries(jsonData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    return searchParams;
  }, [
    cart,
    documentID,
    clientCode,
    sessionKey,
    pointOfSaleID,
    customerID,
    employeeID,
    warehouseID,
  ]);

  const latestRequestBody = useLatest(requestBody);

  React.useEffect(() => {
    function warnAboutClosing(event: BeforeUnloadEvent) {
      // Nothing to save
      if (latestRequestBody.current === undefined) {
        return undefined;
      }

      // eslint-disable-next-line no-param-reassign
      event.returnValue = true;
      event.preventDefault();
      return (
        'Do not close the POS without finishing the sale. Failure to comply will result in' +
        'the current sale being saved as a pending sale and the bypass attempt being logged'
      );
    }

    async function saveCart() {
      if (latestRequestBody.current === undefined) {
        return;
      }

      // Lock the POS while we are saving to avoid race conditions
      dispatch(
        addWarning('[security]: Saving cart', {
          dismissible: false,
          errorType: 'security-autosave',
        }),
      );
      const body = latestRequestBody.current;
      try {
        await window.fetch(`${proxy}https://${clientCode}.erply.com/api/`, {
          method: 'POST',
          body,
          keepalive: true,
        });
        // Once saved, reset the cart to ensure it isn't duplicated (if pos still open or user about to log in again)
        // Also automatically open the pending sales menu, so if the user still has the POS open they can quickly pick up their sale again
        await Promise.all([
          dispatch(resetShoppingCart()),
          dispatch(fetchPendingSalesAndOpenModal()),
        ]);
      } catch (err) {
        // If it fails, we're screwed. Log it to sentry to sentry so we can at least know it's happening
        Sentry.captureException(
          new Error('Security plugin failed to autosave sale', {
            cause: err,
          }),
          {
            contexts: {
              autosavePayload: Object.fromEntries(body?.entries() ?? []),
            },
          },
        );
      } finally {
        dispatch(dismissType('security-autosave'));
      }
    }
    function saveCartIfHidden() {
      if (document.hidden) {
        saveCart();
      }
    }
    // Use beforeunload and unload to perform an emergency save before the POS is closed
    window.addEventListener('beforeunload', warnAboutClosing);
    window.addEventListener('visibilitychange', saveCartIfHidden);
    return () => {
      window.removeEventListener('beforeunload', warnAboutClosing);
      window.removeEventListener('visibilitychange', saveCartIfHidden);
      saveCart();
    };
  }, [latestRequestBody, clientCode, dispatch]);
  return null;
}

const SecurityPlugin: PosPlugin<Configuration> = {
  id: pluginID,
  name: 'Security plugin',
  keywords: ['security', 'manager override', 'manager', 'override', 'rights'],
  infoMDUrl: description,
  getStatus: state => {
    const conf = getConfig(state);
    const { managerGroups } = conf;
    const isValid = Object.values(managerGroups).some(val => val);
    return {
      type: isValid ? 'valid' : 'error',
      message: isValid
        ? 'Configure manager groups in plugin configuration'
        : 'At least 1 user group has to be configured as manager group',
    };
  },

  onHardLogout: {
    before: p => async (dispatch, getState) => {
      // NB: Hard logout MUST be blocked.
      // If it is not, sendBeacon will not be able to update the document because the session key will be invalidated
      const cart = getShoppingCartForSalesDocument(getState());
      if (
        cart.length &&
        getIsOverrideActionEnabled('startNewSale')(getState())
      ) {
        dispatch(
          addError(
            'Not allowed to log out without finishing/saving the current sale first',
          ),
        );
        throw new Error(
          'Not allowed to log out without finishing/saving the current sale first',
        );
      }
    },
  },
  onSoftLogout: {
    before: p => async (dispatch, getState) => {
      const cart = getShoppingCartForSalesDocument(getState());
      if (
        cart.length &&
        getIsOverrideActionEnabled('startNewSale')(getState())
      ) {
        dispatch(
          addError(
            'Not allowed to log out without finishing/saving the current sale first',
          ),
        );
        throw new Error(
          'Not allowed to log out without finishing/saving the current sale first',
        );
      }
    },
  },
  /**
   * Track the shopping cart. Intercept attempts to close the page, log out, disable the plugin, etc.
   * If such action is performed, save the current cart as a new pending sale to prevent the cart from being lost
   * If current cart belongs to an existing document, update that document's cart instead
   */
  ComponentHeader: function ComponentHeader({ children }) {
    const preventReducingAmount = useSelector(
      getIsOverrideActionEnabled('startNewSale'),
    );

    return (
      <>
        {preventReducingAmount && <SaveCartOnAttemptedBypass />}
        {children}
      </>
    );
  },

  ComponentConfigurationByLevel: {
    Company: ({
      current = {
        managerGroups: {},
        optionalActions: defaultActionsConfig,
      } as Configuration,
      save,
    }) => {
      const { t } = useTranslation(['security', 'common']);
      const [groups, setGroups] = useState(current?.managerGroups ?? {});
      const [actions, setActions] = useState(
        R.mergeDeepLeft(current?.optionalActions ?? {}, defaultActionsConfig),
      );
      const { value: allGroups = [], loading } = useAsync(
        () => getUserGroups(),
        [getUserGroups],
      );

      const onGroupChange = e => {
        const { value, name } = e.target;
        setGroups({
          ...groups,
          [name]: value,
        });
      };
      const onActionsChange = (key: string, value: boolean) => {
        setActions({
          ...actions,
          [key]: value,
        });
      };

      const configUnchanged =
        R.equals(groups, current?.managerGroups) &&
        R.equals(actions, current?.optionalActions ?? {});
      const saveButtonDisabled =
        configUnchanged || Object.values(groups).every(val => !val);

      return (
        <div>
          <UIButton
            text={`${t('save_changes', { ns: 'common' })}`}
            action={() =>
              save({
                managerGroups: groups,
                optionalActions: actions,
              })
            }
            disabled={saveButtonDisabled}
          />
          <h3>{t('titles.managerGroups')}</h3>

          <Loader show={loading} loadingText="Loading user groups...">
            {allGroups.map(group => {
              return (
                <InputField
                  type="checkbox"
                  name={group.userGroupID}
                  value={groups[group.userGroupID] || false}
                  onChange={onGroupChange}
                >
                  {group.name}
                </InputField>
              );
            })}
          </Loader>
          <h3>{t('titles.configurableActions')}</h3>
          <Box display="flex" flexDirection="column">
            {configurableActions.map(ca => (
              <FormControlLabel
                control={
                  <Checkbox
                    key={ca}
                    name={ca}
                    value={actions[ca] ?? false}
                    checked={actions[ca] ?? false}
                    onChange={() => onActionsChange(ca, !actions[ca])}
                  />
                }
                label={t(`configurableActions.${ca}`)}
              />
            ))}
          </Box>
        </div>
      );
    },
  },
  onUpdateOrderAmount: {
    // eslint-disable-next-line consistent-return
    before: ({ amount, orderIndex }) => async (dispatch, getState) => {
      const order = getProductInOrderByIndex(orderIndex)(getState());
      const currentAmount = Number(order.amount);
      const numberToSet = Number(amount);

      const isLoweringQty = numberToSet < currentAmount;
      const bothPositive = currentAmount > 0 && numberToSet > 0;
      const bothNegative = currentAmount < 0 && numberToSet < 0;

      const { managerGroups } = getConfig(getState());
      const isOverrideEnabled = getIsOverrideActionEnabled('reducedAmount')(
        getState(),
      );
      // Check specifically if user is LOWERING the amount and not starting the return - starting the return is checked from the main thread aka core POS
      if (
        isOverrideEnabled &&
        isLoweringQty &&
        (bothNegative || bothPositive)
      ) {
        await i18next.loadNamespaces('security');

        return dispatch(
          openManagerOverride([
            {
              key: 'reducedAmount',
              text: i18next.t('security:overrides.reducedAmount'),
              isPermitted: async user => managerGroups[user.groupID],
            },
          ]),
        ) as any;
      }
    },
  },
  selectorOverrides: {
    getFunctionButtons: function blockPickingUpSales(base) {
      return createSelector(
        base,
        getIsOverrideActionEnabled('startNewSale'),
        state => getProductsInShoppingCart(state).length,
        (buttons: GridButtonFunction[], startNewSaleForbidden, cartSize) => {
          if (startNewSaleForbidden && cartSize > 0) {
            const forbidden = [
              'completedSales',
              'pendingSales',
              'layawaySales',
              'orderSales',
              'closeDay',
              'closeDrawer',
              'payAnInvoice',
              'offers',
            ];
            return buttons.map(btn => {
              if (forbidden.includes(btn.id)) {
                return { ...btn, disabled: true };
              }
              return btn;
            });
          }
          return buttons;
        },
      );
    },
  },
  onRemoveOrder: {
    // eslint-disable-next-line consistent-return
    before: () => async (dispatch, getState) => {
      const { managerGroups } = getConfig(getState());

      const isOverrideEnabled = getIsOverrideActionEnabled('rowRemoval')(
        getState(),
      );

      if (isOverrideEnabled) {
        await i18next.loadNamespaces('security');

        return dispatch(
          openManagerOverride([
            {
              key: 'rowRemoval',
              text: i18next.t('security:overrides.rowRemoval'),
              isPermitted: async user => managerGroups[user.groupID],
            },
          ]),
        ) as any;
      }
    },
  },
  onStartNewSale: {
    // eslint-disable-next-line consistent-return
    before: () => async (dispatch, getState) => {
      const { managerGroups } = getConfig(getState());

      const isOverrideEnabled = getIsOverrideActionEnabled('startNewSale')(
        getState(),
      );

      const hasShoppingCart = getHasProductsInShoppingCart(getState());

      if (hasShoppingCart && isOverrideEnabled) {
        await i18next.loadNamespaces('security');
        return dispatch(
          openManagerOverride([
            {
              key: 'startNewSale',
              text: i18next.t('security:overrides.startNewSale'),
              isPermitted: async user => managerGroups[user.groupID],
            },
          ]),
        ) as any;
      }
    },
  },
  onGetIsAccountSaleOverCreditLimit: {
    on: (_p, exceedsLimit) => async (
      dispatch: ThunkDispatch<RootState, unknown, Action>,
      getState,
    ) => {
      if (!exceedsLimit) {
        return exceedsLimit;
      }
      const { managerGroups } = getConfig(getState());
      const overrideEnabled = getIsOverrideActionEnabled(
        'accountSalesCreditLimit',
      )(getState());

      if (overrideEnabled) {
        try {
          await i18next.loadNamespaces('security');
          await dispatch(
            openManagerOverride([
              {
                key: 'accountSalesCreditLimit',
                text: i18next.t('security:overrides.accountSalesCreditLimit'),
                isPermitted: async user => {
                  const rights = await getRightsForUserID(Number(user.userID));
                  return (
                    managerGroups[user.groupID] ||
                    !!Number(rights.rightGiveCustomerCredit)
                  );
                },
              },
            ]),
          );
          return false;
        } catch (e) {
          // Manager override was cancelled
          return true;
        }
      }
      return exceedsLimit;
    },
  },
  UIReturnTable({ children }) {
    const restricted = useSelector(getIsOverrideActionEnabled('startNewSale'));
    const cartEmpty = useSelector(
      state => getProductsInShoppingCart(state).length === 0,
    );
    const block = restricted && !cartEmpty;

    const dispatch = useDispatch();
    React.useEffect(() => {
      if (block) {
        dispatch(previousModalPage({ component: modalPages.ProductReturn }));
        dispatch(
          addError(
            'Not allowed to start a new sale without saving the current one first',
          ),
        );
      }
    }, [block]);
    if (block) {
      return (
        <h1>
          Not allowed to start a new sale without saving the current one first!
        </h1>
      );
    }
    return children;
  },
};

export default SecurityPlugin;
